import { Divider, Stack, Typography } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { ContextMenuButton } from '@/components/form/baseInputs/Button/ContextMenuButton';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { Card } from '@/components/layout/Card/Card';
import { Markdown } from '@/components/Markdown/Markdown';
import { FlushEditorInner } from '@/components/TextEditor/TextEditor.utils';
import { AiMemoRatingButtons } from '@/modules/memos/components/AiMemoRatingButtons';
import { MemoContextMenuItems } from '@/modules/memos/components/MemoContextMenuItems';
import { COLORS } from '@/styles/tokens/colors';
import { MemoAiRating, MemoPromptMemoPromptType } from '@/types/schema';

import { MemoDetails_MemoFragment } from '../../graphql/MemoDetails.generated';
import { MemoProcessingView } from './MemoProcessingView';

interface MemoContentPanelProps {
  memo: MemoDetails_MemoFragment;
  memoAttributionText: string;
  braintrustLogsUrl?: string;
  canEditHouseholdData: boolean;
  savingMemoRating: boolean;
  isGenerating: boolean;
  disableMemoActions: boolean;
  canDeleteMemo: boolean;
  deletingMemo: boolean;
  duplicatingMemo: boolean;
  onRateMemo: (rating: MemoAiRating) => void;
  onRegenerateMemo: () => void;
  onEditMemo: () => void;
  onExportMemoToPdf: () => void;
  onExportMemoToWord: () => void;
  onCopyMemoToClipboard: () => void;
  onDuplicateMemo: () => void;
  onDeleteMemo: () => void;
}

export function MemoContentPanel({
  memo,
  memoAttributionText,
  braintrustLogsUrl,
  canEditHouseholdData,
  savingMemoRating,
  isGenerating,
  disableMemoActions,
  canDeleteMemo,
  deletingMemo,
  duplicatingMemo,
  onRateMemo,
  onRegenerateMemo,
  onEditMemo,
  onExportMemoToPdf,
  onExportMemoToWord,
  onCopyMemoToClipboard,
  onDuplicateMemo,
  onDeleteMemo,
}: MemoContentPanelProps) {
  // CUSTOM memos are agent-authored (e.g. the household chat agent's
  // create-custom-memo tool): there is no user-selected prompt to re-run, so
  // regeneration isn't offered.
  const canRegenerateMemo =
    memo.memoPrompt?.memoPromptType !== MemoPromptMemoPromptType.Custom;

  return (
    <Card
      variant="outlined"
      data-name="MemoContentPanel"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 610,
      }}
    >
      {isGenerating ? (
        <Stack
          flexGrow={1}
          minHeight={610}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <MemoProcessingView />
        </Stack>
      ) : (
        <Stack spacing={2} height="100%" p={2}>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              {canEditHouseholdData && canRegenerateMemo && (
                <Button
                  startIcon={Stars01Icon}
                  variant="secondary"
                  size="sm"
                  onClick={onRegenerateMemo}
                  disabled={disableMemoActions}
                  iconProps={{ color: COLORS.TEAL['600'], size: 16 }}
                >
                  Regenerate
                </Button>
              )}
              {canEditHouseholdData && (
                <IconButton
                  icon={Edit02Icon}
                  ariaLabel="Edit memo"
                  variant="secondary"
                  size="sm"
                  onClick={onEditMemo}
                  disabled={disableMemoActions}
                />
              )}
              <ContextMenuButton disabled={disableMemoActions}>
                <MemoContextMenuItems
                  braintrustLogsUrl={braintrustLogsUrl}
                  onExportMemoToPdf={onExportMemoToPdf}
                  onExportMemoToWord={onExportMemoToWord}
                  onCopyMemoToClipboard={onCopyMemoToClipboard}
                  onDuplicateMemo={
                    canEditHouseholdData ? onDuplicateMemo : undefined
                  }
                  onDeleteMemo={onDeleteMemo}
                  deletingMemo={deletingMemo}
                  duplicatingMemo={duplicatingMemo}
                  disableDeleteMemo={disableMemoActions}
                  showDeleteMemo={canDeleteMemo}
                />
              </ContextMenuButton>
            </Stack>
          </Stack>
          {memo.content ? (
            <Markdown
              markdown={memo.content}
              EditorInner={FlushEditorInner}
              preserveNewLines={true}
            />
          ) : (
            <Typography variant="body-xs">No memo content.</Typography>
          )}

          <Stack mt="auto" spacing={2}>
            <Divider />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
            >
              <Typography variant="body-xs">{memoAttributionText}</Typography>
              <AiMemoRatingButtons
                rating={memo.aiRating}
                disabled={savingMemoRating}
                onRateMemo={onRateMemo}
              />
            </Stack>
          </Stack>
        </Stack>
      )}
    </Card>
  );
}
